import type { Fragment, Mark, Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type { InlineItem, MarkSpan, TextRun } from './types';

/**
 * Walks a fragment's children and coalesces consecutive text nodes into TextRuns.
 * Non-text inline nodes (hard_break, emoji, mention, ...) and block nodes pass
 * through as `{ kind: "node" }` entries.
 *
 * Spans carry the FULL mark set from the underlying text nodes (no filtering).
 * Callers that want to ignore certain mark types (e.g. annotations) do so
 * locally at comparison and slice-construction time, so the unfiltered marks
 * remain available for inheritance.
 *
 * Mirrors `combineConsecutiveTextNodesIntoIntermediate` in the Kotlin port.
 */
export function combineInlineTextRuns(content: Fragment): InlineItem[] {
	const out: InlineItem[] = [];
	let acc: TextRun | null = null;

	for (let i = 0; i < content.childCount; i++) {
		const child = content.child(i);
		if (child.isText && child.text !== undefined) {
			const text = child.text;
			if (acc === null) {
				acc = {
					kind: 'textRun',
					text,
					spans: [{ start: 0, end: text.length, marks: child.marks }],
				};
			} else {
				const start = acc.text.length;
				acc.text += text;
				acc.spans.push({ start, end: acc.text.length, marks: child.marks });
			}
		} else {
			if (acc) {
				out.push(acc);
				acc = null;
			}
			out.push({ kind: 'node', node: child });
		}
	}
	if (acc) out.push(acc);
	return out;
}

/**
 * Reconstructs PM text nodes for a sub-range `[from, to)` of a TextRun's text,
 * preserving the marks active in that range as encoded by `spans`.
 *
 * If `extraMarks` is provided, each output text node has those marks merged in
 * (in addition to its native span marks). This is how ignored marks get
 * INHERITED into newly emitted slice content: when we Replace a region that
 * had an annotation in pmBase with text from pmNew (which has no annotation),
 * we add the annotation back via extraMarks so it lands on the new text.
 */
export function textNodesFromRun(
	run: TextRun,
	from: number,
	to: number,
	mkText: (text: string, marks?: readonly Mark[]) => PMNode,
	extraMarks: readonly Mark[] = [],
): PMNode[] {
	if (from >= to) return [];
	const out: PMNode[] = [];
	let pos = from;
	while (pos < to) {
		const span = spanCovering(run.spans, pos);
		const segEnd = Math.min(span?.end ?? to, to);
		const segText = run.text.slice(pos, segEnd);
		if (segText.length > 0) {
			const baseMarks = span?.marks ?? [];
			const marks = extraMarks.length === 0 ? baseMarks : mergeMarks(baseMarks, extraMarks);
			out.push(mkText(segText, marks));
		}
		pos = segEnd;
	}
	return out;
}

/**
 * Returns the marks active at `pos` within a TextRun, filtered by `keep`.
 * Used to look up ignored marks at an old-doc position so they can be
 * re-applied via `extraMarks` to a Replace/Insert slice.
 */
export function marksAtPosition(
	run: TextRun,
	pos: number,
	keep: ReadonlySet<string>,
): readonly Mark[] {
	if (keep.size === 0) return [];
	const span = spanCovering(run.spans, pos);
	if (!span) return [];
	return span.marks.filter((m) => keep.has(m.type.name));
}

function mergeMarks(a: readonly Mark[], b: readonly Mark[]): Mark[] {
	const out = [...a];
	for (const m of b) {
		if (!out.some((existing) => existing.eq(m))) out.push(m);
	}
	return out;
}

function spanCovering(spans: readonly MarkSpan[], pos: number): MarkSpan | null {
	// Linear scan is fine for the typical case (one or two text runs per paragraph).
	// Switch to binary search if benchmarks show this matters.
	for (const s of spans) {
		if (pos >= s.start && pos < s.end) return s;
	}
	return null;
}
